import { useDebounceValue, useWindowSize } from 'usehooks-ts';
import { getCardsCountInRow } from '@/components/AdCards/AdCards.helpers';
import {
  DEFAULT_ROWS_COUNT_BEFORE_BANNER,
  ROWS_COUNT_BEFORE_BANNER,
  ROWS_COUNT_BEFORE_DESKTOP_IN_LIST_BANNER,
} from '@/components/AdCards/AdCards.constants';

interface Props {
  bannerInList?: (arCn: number) => React.ReactNode;
  inListDesktopBanner?: React.ReactNode;
  isDesktop: boolean;
  isBannerInListAfterThreeRows?: boolean;
  sectionIndex?: number;
}

export const useBannerPosition = ({
  bannerInList,
  inListDesktopBanner,
  isDesktop,
  isBannerInListAfterThreeRows,
  sectionIndex,
}: Props) => {
  const { width } = useWindowSize();
  const [debouncedWidth] = useDebounceValue(width, 200);

  return (
    idx: number
  ): {
    isMobileBannerRenders: boolean;
    isDesktopBannerRenders: boolean;
    isFirstBanner: boolean;
  } => {
    const cardsCountInRow = getCardsCountInRow(debouncedWidth);

    const isFirstSection = sectionIndex === 0 || !sectionIndex;

    const isBannerPosition =
      idx % (DEFAULT_ROWS_COUNT_BEFORE_BANNER * cardsCountInRow) === 0 ||
      (!!isBannerInListAfterThreeRows &&
        cardsCountInRow * ROWS_COUNT_BEFORE_BANNER === idx);

    const isDesktopBannerPosition =
      sectionIndex === 0 &&
      !!inListDesktopBanner &&
      cardsCountInRow * ROWS_COUNT_BEFORE_DESKTOP_IN_LIST_BANNER === idx;

    const isDesktopBannerRenders =
      isDesktop && isDesktopBannerPosition && !!inListDesktopBanner;

    const isMobileBannerRenders =
      idx !== 0 && !isDesktop && isBannerPosition && !!bannerInList;

    const isFirstBannerOnMobile = isBannerInListAfterThreeRows
      ? idx === cardsCountInRow * ROWS_COUNT_BEFORE_BANNER
      : idx === cardsCountInRow * DEFAULT_ROWS_COUNT_BEFORE_BANNER;

    const isFirstBannerOnDesktop =
      idx === cardsCountInRow * ROWS_COUNT_BEFORE_DESKTOP_IN_LIST_BANNER;

    const isFirstBanner =
      isFirstSection &&
      (isDesktop ? isFirstBannerOnDesktop : isFirstBannerOnMobile);

    return {
      isDesktopBannerRenders,
      isMobileBannerRenders,
      isFirstBanner,
    };
  };
};
